import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import Avatar from "../components/Avatar";
import ShareLineupCard from "../components/ShareLineupCard";
import { getMyLineups, getProfileStats } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Coins, Trophy, Crown, Sparkles, Share2, Clock, Users, ArrowRight, TrendingUp, Medal, AlertCircle, ChevronDown, ChevronUp, Flame } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

function timeLeft(end) {
  if (!end) return "—";
  const ms = new Date(end) - new Date();
  if (ms <= 0) return "Ended";
  const d = Math.floor(ms / (1000 * 60 * 60 * 24));
  const h = Math.floor((ms / (1000 * 60 * 60)) % 24);
  return d > 0 ? `${d}d ${h}h` : `${h}h`;
}

export default function MySeason() {
  const { user } = useAuth();
  const [lineups, setLineups] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareId, setShareId] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([getMyLineups(), getProfileStats()])
      .then(([l, s]) => { setLineups(l); setStats(s); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const live = lineups.filter(l => l.contest && l.contest.status !== "settled");
  const settled = lineups.filter(l => l.contest && l.contest.status === "settled");

  return (
    <div className="min-h-screen pb-safe" data-testid="my-circles-page">
      <Header title="My Circles" />
      <div className="max-w-xl mx-auto px-4 pt-4 space-y-4">
        {/* Wallet hero */}
        <section className="glass-strong rounded-3xl p-5 relative overflow-hidden">
          <div aria-hidden className="pointer-events-none absolute -top-12 -right-14 w-48 h-48 rounded-full opacity-30" style={{ background: "radial-gradient(circle,#B026FF,transparent 70%)" }} />
          <p className="text-xs uppercase tracking-widest text-white/50 font-bold">Fanpuff Credits Wallet</p>
          <div className="flex items-baseline gap-2 mt-1">
            <Coins className="w-6 h-6 text-[#00FF66]" />
            <span className="font-display text-4xl font-bold neon-up font-mono-brand">{fmt(user?.fan_credits || 0)}</span>
          </div>
          <p className="text-[11px] text-white/50 mt-1">Virtual balance · Non-cashable · Free to play</p>
        </section>

        {/* Stat strip */}
        <div className="grid grid-cols-3 gap-2">
          <Stat icon={Users} label="Circles" val={stats?.lineups ?? 0} color="#00F0FF" />
          <Stat icon={Trophy} label="Live" val={live.length} color="#00FF66" />
          <Stat icon={Sparkles} label="Best Pts" val={Math.round(stats?.best_lineup_points ?? 0)} color="#FFD600" />
        </div>

        {/* Live circles */}
        <section data-testid="live-circles-section">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-display text-xl font-bold flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-[#00FF66] animate-pulse" />
              Live Circles
            </h2>
            <Link to="/contests" className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1" data-testid="my-circles-browse">
              Browse <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {loading ? (
            <div className="glass rounded-2xl p-5 text-sm text-white/60 text-center">Loading…</div>
          ) : live.length === 0 ? (
            <div className="glass rounded-2xl p-6 text-center" data-testid="empty-live">
              <AlertCircle className="w-8 h-8 mx-auto text-white/30" />
              <p className="font-display text-base font-bold mt-2">No live circles yet</p>
              <p className="text-xs text-white/55 mt-1">Join a contest to lock your first 6-star Influencer Circle.</p>
              <Link
                to="/contests"
                className="mt-3 inline-flex items-center gap-1 px-5 py-2 rounded-full text-black font-bold text-sm"
                style={{ background: "linear-gradient(90deg,#00F0FF,#B026FF)" }}
                data-testid="empty-live-cta"
              >
                Build your first Circle <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {live.map(l => <LiveCircleCard key={l.lineup_id} l={l} onShare={() => setShareId(l.lineup_id)} />)}
            </div>
          )}
        </section>

        {/* Settled circles */}
        {settled.length > 0 && (
          <section data-testid="settled-circles-section">
            <h2 className="font-display text-xl font-bold flex items-center gap-2 mb-2">
              <Medal className="w-5 h-5 text-[#FFD600]" /> Settled Circles
            </h2>
            <div className="space-y-3">
              {settled.map(l => <SettledCircleCard key={l.lineup_id} l={l} />)}
            </div>
          </section>
        )}

        <div className="h-10" />
      </div>
      <BottomNav />
      <ShareLineupCard open={!!shareId} lineupId={shareId} onClose={() => setShareId(null)} />
    </div>
  );
}

function LiveCircleCard({ l, onShare }) {
  const c = l.contest;
  const rank = l.rank || 0;
  const total = l.total_lineups || 0;
  const pct = total > 0 ? Math.round((rank / total) * 100) : 0;
  const inPayout = l.estimated_payout > 0;
  const tone = inPayout ? "#00FF66" : "#B026FF";
  const [open, setOpen] = useState(false);
  return (
    <div className="glass rounded-3xl overflow-hidden" data-testid={`live-circle-${l.lineup_id}`}>
      <Link to={`/contests/${c.contest_id}`} className="block">
        <div className="relative h-24 overflow-hidden">
          {c.image_url ? <img src={c.image_url} className="w-full h-full object-cover opacity-80" alt="" /> : <div className="w-full h-full bg-white/5" />}
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,transparent 30%, rgba(5,5,8,0.96))" }} />
          <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
            <span className="chip text-[10px]" style={{ background: "#00FF6622", color: "#00FF66", border: "1px solid #00FF6655" }}>● Live</span>
            <span className="chip text-[10px]" style={{ background: "#00F0FF15", color: "#00F0FF", border: "1px solid #00F0FF44" }}>{c.mode}</span>
          </div>
          <div className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 text-[10px] text-white/80">
            <Clock className="w-3 h-3" /> {timeLeft(c.end_date)}
          </div>
          <div className="absolute bottom-2 left-3 right-3">
            <p className="font-display text-base font-bold leading-tight truncate">{c.name}</p>
          </div>
        </div>
      </Link>
      <div className="p-3 grid grid-cols-3 gap-2">
        <div className="rounded-xl p-2 text-center" style={{ background: `${tone}15`, border: `1px solid ${tone}44` }} data-testid={`live-rank-${l.lineup_id}`}>
          <p className="text-[9px] uppercase tracking-widest text-white/55 font-bold">Your rank</p>
          <p className="font-display text-base font-bold" style={{ color: tone }}>#{rank}<span className="text-white/45 text-[10px]"> / {total}</span></p>
        </div>
        <div className="rounded-xl p-2 text-center" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
          <p className="text-[9px] uppercase tracking-widest text-white/55 font-bold">Points</p>
          <p className="font-display text-base font-bold font-mono-brand">{Number(l.points || 0).toFixed(0)}</p>
        </div>
        <div className="rounded-xl p-2 text-center" style={{ background: inPayout ? "rgba(255,214,0,0.12)" : "rgba(255,255,255,0.04)", border: `1px solid ${inPayout ? "rgba(255,214,0,0.35)" : "rgba(255,255,255,0.08)"}` }} data-testid={`live-payout-${l.lineup_id}`}>
          <p className="text-[9px] uppercase tracking-widest font-bold" style={{ color: inPayout ? "#FFD600" : "rgba(255,255,255,0.55)" }}>Est. win</p>
          <p className="font-display text-base font-bold font-mono-brand" style={{ color: inPayout ? "#FFD600" : "#fff" }}>
            {inPayout ? `+${fmt(l.estimated_payout)}` : "Out of zone"}
          </p>
        </div>
      </div>
      <div className="px-3 pb-3 flex items-center justify-between text-[11px] gap-2">
        <span className="inline-flex items-center gap-1 text-white/55">
          <TrendingUp className="w-3 h-3" /> Top {Math.max(1, pct)}% in this Circle
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setOpen(o => !o)}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-bold"
            style={{ background: open ? "rgba(0,240,255,0.18)" : "rgba(255,255,255,0.05)", border: `1px solid ${open ? "rgba(0,240,255,0.5)" : "rgba(255,255,255,0.12)"}`, color: open ? "#00F0FF" : "#fff" }}
            data-testid={`view-circle-${l.lineup_id}`}
            aria-expanded={open}
          >
            {open ? <><ChevronUp className="w-3 h-3" /> Hide team</> : <><Users className="w-3 h-3" /> View team</>}
          </button>
          <button
            onClick={(e) => { e.preventDefault(); onShare(); }}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-bold"
            style={{ background: "linear-gradient(135deg,#B026FF33,#00F0FF33)", border: "1px solid rgba(255,255,255,0.15)" }}
            data-testid={`share-circle-${l.lineup_id}`}
          >
            <Share2 className="w-3 h-3" /> Share
          </button>
        </div>
      </div>
      {open && <PicksList picks={l.picks_full || []} captainId={l.captain_id} vcId={l.vice_captain_id} testidPrefix={l.lineup_id} />}
    </div>
  );
}

function PicksList({ picks, captainId, vcId, testidPrefix }) {
  if (!picks.length) {
    return <div className="px-3 pb-3 text-xs text-white/45">No picks recorded.</div>;
  }
  // total points (including multipliers)
  const total = picks.reduce((s, p) => s + (p.points || 0), 0);
  return (
    <div className="border-t border-white/8 px-3 py-3 space-y-2" data-testid={`picks-list-${testidPrefix}`}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-widest text-white/55 font-bold">Your 6-star Circle</p>
        <p className="text-[10px] text-white/55 font-mono-brand">Σ {total.toFixed(0)} pts</p>
      </div>
      <div className="space-y-1.5">
        {picks.map((p) => {
          const isC = p.is_captain;
          const isV = p.is_vice_captain;
          const role = isC ? { color: "#FFD600", label: "C", multi: "2×" } : isV ? { color: "#00F0FF", label: "VC", multi: "1.5×" } : null;
          const sharePct = total > 0 ? Math.round((p.points / total) * 100) : 0;
          return (
            <div
              key={p.index_id}
              className="flex items-center gap-2.5 rounded-xl p-2"
              style={{ background: role ? `${role.color}10` : "rgba(255,255,255,0.03)", border: `1px solid ${role ? `${role.color}44` : "rgba(255,255,255,0.06)"}` }}
              data-testid={`pick-row-${testidPrefix}-${p.index_id}`}
            >
              <Avatar name={p.name} category={p.category} symbol={p.symbol} size={36} rounded={10} showSymbol={false} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="text-sm font-semibold truncate">{p.name}</p>
                  {role && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded font-bold" style={{ background: `${role.color}33`, color: role.color, border: `1px solid ${role.color}66` }}>
                      {role.label} {role.multi}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-[10px] text-white/50">
                  <span className="inline-flex items-center gap-0.5"><Flame className="w-2.5 h-2.5 text-[#FF0055]" /> {p.heat_score?.toFixed?.(0) ?? p.heat_score}</span>
                  <span className={p.change_24h >= 0 ? "text-[#00FF66]" : "text-[#FF0055]"}>
                    {p.change_24h >= 0 ? "+" : ""}{Number(p.change_24h || 0).toFixed(1)}%
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className="font-display text-sm font-bold font-mono-brand" style={{ color: role ? role.color : "#fff" }}>
                  {p.points.toFixed(0)}
                </p>
                <p className="text-[9px] text-white/40 font-mono-brand">{sharePct}% of pts</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SettledCircleCard({ l }) {
  const c = l.contest;
  const net = l.settled_net ?? 0;
  const isWin = net > 0;
  const isLoss = net < 0;
  const tone = isWin ? "#00FF66" : isLoss ? "#FF0055" : "#B026FF";
  const [open, setOpen] = useState(false);
  return (
    <div className="glass rounded-2xl overflow-hidden" data-testid={`settled-circle-${l.lineup_id}`}>
      <button onClick={() => setOpen(o => !o)} className="w-full p-3 flex items-center gap-3 hover:bg-white/5 text-left" aria-expanded={open}>
        {c.image_url ? <img src={c.image_url} className="w-14 h-14 rounded-xl object-cover" alt="" /> : <div className="w-14 h-14 rounded-xl bg-white/5" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="font-display font-semibold truncate">{c.name}</p>
            {l.rank <= 3 && <Crown className="w-3.5 h-3.5 text-[#FFD600]" />}
          </div>
          <p className="text-[11px] text-white/50">Final rank #{l.rank || "—"} / {l.total_lineups || 0} · {Number(l.points || 0).toFixed(0)} pts</p>
        </div>
        <div className="text-right">
          <p className="font-display text-base font-bold" style={{ color: tone }}>
            {isWin ? "+" : ""}{fmt(net)}
          </p>
          <p className="text-[10px] uppercase tracking-widest text-white/45">credits</p>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-white/55 ml-1" /> : <ChevronDown className="w-4 h-4 text-white/55 ml-1" />}
      </button>
      {open && <PicksList picks={l.picks_full || []} captainId={l.captain_id} vcId={l.vice_captain_id} testidPrefix={l.lineup_id} />}
    </div>
  );
}

function Stat({ icon: Icon, label, val, color = "#FFD600" }) {
  return (
    <div className="glass rounded-2xl p-3" style={{ border: `1px solid ${color}33` }}>
      <div className="flex items-center gap-1 text-[10px] tracking-widest uppercase text-white/55 font-bold">
        <Icon className="w-3 h-3" style={{ color }} /> {label}
      </div>
      <p className="font-display text-lg font-semibold mt-1">{typeof val === "number" ? val.toLocaleString("en-IN") : val}</p>
    </div>
  );
}
